import React, { useState, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { collection, addDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './postSchedulerCalendar.css';
import { softPlatformIcon } from './socialSoftPalette';
import { LinkedInIcon, FacebookIcon, InstagramIcon, TwitterIcon, PinterestIcon, PostIcon, CalendarIcon, TipIcon, ImageIcon, NewsFeedIcon, QuickIcon, PremiumIcon, HourglassIcon, EmptyTrayIcon, CheckIcon } from '../icons';

interface ScheduledPost {
  id?: string;
  userId: string;
  content: string;
  platforms: string[];
  scheduledFor: Date;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  imageUrl?: string;
  imageUrls?: string[];
  autoShareToNewsFeed?: boolean;
  createdAt: Date;
}

interface PostSchedulerProps {
  userId: string;
}

const PostScheduler: React.FC<PostSchedulerProps> = ({ userId }) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [content, setContent] = useState('');
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [autoShareToNewsFeed, setAutoShareToNewsFeed] = useState(true);
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', Icon: LinkedInIcon, color: softPlatformIcon('linkedin') },
    { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, color: softPlatformIcon('facebook') },
    { id: 'instagram', name: 'Instagram', Icon: InstagramIcon, color: softPlatformIcon('instagram') },
    { id: 'twitter', name: 'Twitter/X', Icon: TwitterIcon, color: softPlatformIcon('twitter') },
    { id: 'pinterest', name: 'Pinterest', Icon: PinterestIcon, color: softPlatformIcon('pinterest') },
  ];

  useEffect(() => {
    loadScheduledPosts();
  }, [userId]);

  const loadScheduledPosts = async () => {
    try {
      const postsQuery = query(
        collection(db, 'scheduledPosts'),
        where('userId', '==', userId),
        orderBy('scheduledFor', 'asc')
      );
      const snapshot = await getDocs(postsQuery);
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledFor: doc.data().scheduledFor.toDate(),
        createdAt: doc.data().createdAt.toDate()
      })) as ScheduledPost[];
      setScheduledPosts(posts);
    } catch (error) {
      console.error('Error loading scheduled posts:', error);
    }
  };

  const handleSchedulePost = async () => {
    console.log('🚀 handleSchedulePost called');
    console.log('📝 Content:', content);
    console.log('📱 Selected platforms:', selectedPlatforms);
    console.log('👤 User ID:', userId);
    
    if (!content.trim() || selectedPlatforms.length === 0) {
      alert('Vul content in en selecteer minimaal 1 platform');
      return;
    }

    setLoading(true);
    try {
      console.log('✅ Validation passed, checking post limit...');
      
      // Check post limit before scheduling post
      const { default: postLimitService } = await import('../../services/postLimitService');
      await postLimitService.enforcePostLimit(userId);
      
      console.log('✅ Post limit check passed');
      
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const scheduledDateTime = new Date(selectedDate);
      scheduledDateTime.setHours(hours, minutes, 0, 0);
      
      console.log('📅 Scheduled for:', scheduledDateTime);

      // Upload images if any
      let imageUrls: string[] = [];
      if (selectedImages.length > 0) {
        console.log(`📤 Uploading ${selectedImages.length} images...`);
        for (const image of selectedImages) {
          const timestamp = Date.now();
          const randomId = Math.random().toString(36).substring(7);
          const imageRef = ref(storage, `scheduled-posts/${userId}/${timestamp}-${randomId}-${image.name}`);
          await uploadBytes(imageRef, image);
          const downloadURL = await getDownloadURL(imageRef);
          imageUrls.push(downloadURL);
          console.log(`✅ Image uploaded: ${downloadURL}`);
        }
        console.log('✅ All images uploaded:', imageUrls);
      }

      // Build post object without undefined values (Firestore doesn't accept undefined)
      const newPost: any = {
        userId,
        content,
        platforms: selectedPlatforms,
        scheduledFor: scheduledDateTime,
        status: 'scheduled' as const,
        autoShareToNewsFeed,
        createdAt: new Date()
      };

      // Only add imageUrls if there are images
      if (imageUrls.length > 0) {
        newPost.imageUrls = imageUrls;
      }

      console.log('💾 Saving post to Firestore:', newPost);
      console.log('📊 Post object keys:', Object.keys(newPost));
      console.log('📊 Post object values:', Object.values(newPost).map(v => typeof v));
      
      const docRef = await addDoc(collection(db, 'scheduledPosts'), newPost);
      
      console.log('✅ Post saved with ID:', docRef.id);
      
      // Reset form
      setContent('');
      setSelectedPlatforms([]);
      setAutoShareToNewsFeed(true);
      setSelectedImages([]);
      setImagePreviewUrls([]);
      
      console.log('🔄 Reloading scheduled posts...');
      await loadScheduledPosts();
      
      console.log('✅ Post successfully scheduled!');
      alert('✅ Post ingepland!');
    } catch (error: any) {
      console.error('❌ Error scheduling post:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(`❌ Fout bij inplannen van post: ${error.message || 'Onbekende fout'}`);
    } finally {
      setLoading(false);
      console.log('🏁 handleSchedulePost finished');
    }
  };

  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length + selectedImages.length > 9) {
      alert('⚠️ Maximaal 9 afbeeldingen toegestaan');
      return;
    }

    setSelectedImages(prev => [...prev, ...files]);

    // Create preview URLs
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrls(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const getPostsForDate = (date: Date) => {
    return scheduledPosts.filter(post => {
      const postDate = new Date(post.scheduledFor);
      return postDate.toDateString() === date.toDateString();
    });
  };

  const tileContent = ({ date, view }: any) => {
    if (view === 'month') {
      const postsOnDate = getPostsForDate(date);
      if (postsOnDate.length > 0) {
        return (
          <div className="flex justify-center mt-1">
            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#7A9488] rounded-full">
              {postsOnDate.length}
            </span>
          </div>
        );
      }
    }
    return null;
  };

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Create Post */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2"><PostIcon size={24} weight="regular" className="text-gray-600" /> Plan Nieuwe Post</h2>
          
          {/* Platform Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Selecteer Platforms *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {platforms.map(platform => (
                <button
                  key={platform.id}
                  onClick={() => togglePlatform(platform.id)}
                  className={`
                    flex items-center p-4 rounded-lg border-2 transition-all
                    ${selectedPlatforms.includes(platform.id)
                      ? 'border-[#7A9488] bg-stone-50'
                      : 'border-gray-200 hover:border-gray-300'
                    }
                  `}
                >
                  <platform.Icon size={28} weight="regular" style={{ color: platform.color }} />
                  <div className="text-left flex-1">
                    <div className="font-semibold text-gray-900">{platform.name}</div>
                    {selectedPlatforms.includes(platform.id) && (
                      <div className="text-xs text-[#4F6660] flex items-center gap-0.5"><CheckIcon size={12} weight="bold" /> Geselecteerd</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Post Content *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Schrijf je post hier..."
              rows={6}
              maxLength={3000}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488] focus:border-transparent resize-none"
            />
            <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1"><TipIcon size={14} weight="regular" className="text-gray-500" /> Tip: Gebruik hashtags voor meer engagement</span>
              <span>{content.length}/3000</span>
            </div>
          </div>

          {/* Image Upload */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <span className="flex items-center gap-1"><ImageIcon size={16} weight="regular" className="text-gray-600" /> Afbeeldingen (optioneel, max 9)</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-[#9AAF88] transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="hidden"
                id="image-upload"
                disabled={selectedImages.length >= 9}
              />
              <label
                htmlFor="image-upload"
                className={`flex flex-col items-center justify-center cursor-pointer ${
                  selectedImages.length >= 9 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <ImageIcon size={36} weight="regular" className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">
                  {selectedImages.length >= 9
                    ? 'Maximaal aantal bereikt'
                    : 'Klik om afbeeldingen te selecteren'}
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  {selectedImages.length}/9 afbeeldingen
                </span>
              </label>
            </div>

            {/* Image Previews */}
            {imagePreviewUrls.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-3">
                {imagePreviewUrls.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-[#4F6660] text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Date and Time Selection */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Datum
              </label>
              <input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => setSelectedDate(new Date(e.target.value))}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Tijd
              </label>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7A9488] focus:border-transparent"
              />
            </div>
          </div>

          {/* Auto-share to NewsFeed */}
          <div className="mb-6 bg-stone-50 border border-stone-200 rounded-lg p-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoShareToNewsFeed}
                onChange={(e) => setAutoShareToNewsFeed(e.target.checked)}
                className="w-5 h-5 text-[#7A9488] border-gray-300 rounded focus:ring-[#7A9488]"
              />
              <div className="ml-3">
                <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  <NewsFeedIcon size={16} weight="regular" className="text-gray-600" /> Ook delen in SuperKonnected Feed
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Deze post wordt ook zichtbaar voor SuperKonnected leden
                </div>
              </div>
            </label>
          </div>

          {/* Branding Info */}
          <div className="mb-6 bg-gradient-to-r from-stone-50 to-stone-100 border border-stone-200 rounded-lg p-4">
            <div className="flex items-start">
              <QuickIcon size={22} weight="regular" className="mr-3 text-[#7A9488] flex-shrink-0" />
              <div className="text-xs text-gray-700">
                <div className="font-semibold mb-1">Gratis gebruiker branding:</div>
                <div className="text-gray-600 italic">
                  "---<br/>
                  SuperKonnected | superkonnected.nl<br/>
                  #SuperKonnected"
                </div>
                <div className="mt-2 text-[#4F6660] flex items-center gap-1">
                  <PremiumIcon size={14} weight="regular" /> Upgrade naar Premium om zonder branding te delen
                </div>
              </div>
            </div>
          </div>

          {/* Schedule Button */}
          <button
            onClick={handleSchedulePost}
            disabled={loading || !content.trim() || selectedPlatforms.length === 0}
            className="w-full bg-gradient-to-r from-[#7A9488] to-[#4F6660] text-white py-3 px-6 rounded-lg font-semibold hover:from-[#4F6660] hover:to-[#4A5A3C] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2"><HourglassIcon size={18} weight="regular" /> Bezig met inplannen...</span>
            ) : (
              <span className="flex items-center justify-center gap-2"><CalendarIcon size={18} weight="regular" /> Plan Post In</span>
            )}
          </button>
        </div>

        {/* Right Column - Calendar & Scheduled Posts */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2"><CalendarIcon size={22} weight="regular" className="text-gray-600" /> Content Kalender</h3>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
            <Calendar
              onChange={(value) => {
                if (value instanceof Date) {
                  setSelectedDate(value);
                }
              }}
              value={selectedDate}
              tileContent={tileContent}
              className="sk-social-calendar border-0"
            />
          </div>

          {/* Posts on Selected Date */}
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Posts op {selectedDate.toLocaleDateString('nl-NL')}
            </h4>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {getPostsForDate(selectedDate).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <EmptyTrayIcon size={36} weight="regular" className="mx-auto mb-2 text-gray-400" />
                  <p>Geen posts ingepland voor deze dag</p>
                </div>
              ) : (
                getPostsForDate(selectedDate).map(post => (
                  <div key={post.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {post.platforms.map(platformId => {
                          const platform = platforms.find(p => p.id === platformId);
                          return platform ? (
                            <platform.Icon key={platformId} size={20} weight="regular" style={{ color: platform.color }} />
                          ) : null;
                        })}
                      </div>
                      <span className="text-xs font-semibold text-[#4F6660] bg-stone-100 px-2 py-1 rounded">
                        {new Date(post.scheduledFor).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-2">{post.content}</p>
                    {post.autoShareToNewsFeed && (
                      <div className="mt-2 flex items-center text-xs text-[#4F6660] gap-1">
                        <NewsFeedIcon size={14} weight="regular" />
                        <span>Ook in NewsFeed</span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostScheduler;

